import type { Theme } from '@mui/material'
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import CameraControls from 'camera-controls'
import { useCallback, useEffect, useState } from 'react'
import { ApplicationTypes } from 'sunlife-domain'
import { PerspectiveCamera } from 'three'
import { v4 } from 'uuid'

import { useLogger, useMountGuard } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../../systems/configurator-interface'
import { FinalizeWishlistSuccessDialogContent } from './dialog-content/FinalizeWishlistSuccessDialogContent'

const IMAGE_HEIGHT = 720
const IMAGE_WIDTH = 1080

export default function FinalizeWishlistDialog() {
  const [finalizationUuid, setFinalizationUuid] = useState<string | null>(null)

  const frontendData = configuratorInterface.useFrontendData()

  const { navigationSystem, storeSystem, configurationId } = configuratorInterface.useDomain()
  const { imageRenderingSystem } = configuratorInterface.useConfigurator()
  const committedState = configuratorInterface.useCommittedState()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const apiClient = configuratorInterface.useAuthorizedClient()
  const isMounted = useMountGuard()
  // const logger = useLogger()

  const generateImages = useCallback(
    async (finalizationUuid: string) => {
      await imageRenderingSystem.generateImages(finalizationUuid, [
        {
          name: 'clientGeneratedImage',
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          getCamera: async scene => {
            const camera = new PerspectiveCamera(45, IMAGE_WIDTH / IMAGE_HEIGHT, 1, 10000)
            const controls = new CameraControls(camera)

            const mesh = scene.getObjectByName(committedState.rootEntityId)

            if (mesh == null) {
              throw new Error('Root entity not found!')
            }

            await controls.fitToSphere(mesh, false)
            await controls.rotateTo(-0.5, 1.25, false)

            // Move camera farther away from the object
            await controls.dollyTo(controls.distance, false)

            // To mock the "animations"
            controls.update(1000000)

            return camera
          },
        },
      ])
    },
    [imageRenderingSystem, committedState.rootEntityId]
  )
  const finalize = useCallback(async () => {
    const finalizationUuid = v4()

    await Promise.all([configurationPersistSystem.save(), generateImages(finalizationUuid)])

    await apiClient.finalize(configurationId, finalizationUuid, {}, undefined, frontendData)

    if (isMounted()) {
      setFinalizationUuid(finalizationUuid)
    }
  }, [apiClient, configurationPersistSystem, generateImages, configurationId, isMounted, frontendData])

  const handleBack = useCallback(() => {
    storeSystem.stageAndCommit([navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.Initial)])
  }, [navigationSystem, storeSystem])

  const logger = useLogger()

  const { mutate, isError, isSuccess } = useMutation({
    mutationKey: ['finalize'],
    mutationFn: finalize,
    onError(error) {
      logger.error('Finalization failed:', {}, error)
    },
  })
  useEffect(() => {
    mutate()
  }, [mutate])
  return (
    <>
      {isSuccess && finalizationUuid && (
        <FinalizeWishlistSuccessDialogContent finalizationUuid={finalizationUuid} onClose={handleBack} />
      )}
      {!isSuccess && (
        <Dialog.ContentLayout
          controlsPosition="end"
          header={
            <Box
              component="div"
              sx={(theme: Theme) => ({
                backgroundColor: theme.palette.background.default,
                aspectRatio: '2.9',
                display: 'grid',
                placeItems: 'center',
                color: 'green',
              })}
            >
              <PhxIcon name="CheckCircle" style={{ fontSize: 200 }} />
            </Box>
          }
          body={
            <>
              <Stack direction="column" spacing={1} sx={{ pl: 0.5 }}>
                <Typography>Creating new Wishlist Item...</Typography>
                <LinearProgress />
                {isError && (
                  <Alert severity="error">
                    Adding to Wishlist failed. Please try again later. If the issue persists, contact website support.
                  </Alert>
                )}
              </Stack>
            </>
          }
          controls={
            <Button variant="text" onClick={handleBack} startIcon={<PhxIcon name="ArrowLeft" />}>
              Back to Design
            </Button>
          }
        />
      )}
    </>
  )
}
